// TelcoTrack — endpoint IDs, orientation, patch/weld writes, splitter-path validation.

import {
  getRozvadecCassetteType,
  rozvadecKm5AdapterPosition,
  rozvadecKm5ConnectorPolarity,
} from './rozvadec-cassette.constants';
import type { RozvadecWeldCassette, RozvadecWeldLayout } from './rozvadec-weld-layout.types';
import type {
  OccupancyCableRole,
  OccupancyConnectionIssue,
  OccupancyConnectionIssueCode,
  OccupancyConnectorChannel,
  OccupancyConnectorSide,
  OccupancyConnectorSideName,
  OccupancyEndpoint,
  OccupancyPatchConnection,
  OccupancySplitterPath,
  OccupancyWeldConnection,
} from './rozvadec-occupancy-connection.types';

export * from './rozvadec-occupancy-connection.types';

const FIBER_PREFIX = 'fiber:';
const CONN_PREFIX = 'cas:';

export function occupancyEndpointId(endpoint: OccupancyEndpoint): string {
  switch (endpoint.kind) {
    case 'cable-fiber':
      return `${FIBER_PREFIX}${endpoint.role ?? 'unknown'}:${endpoint.cableId}:${endpoint.physicalFiber}`;
    case 'cassette-connector': {
      const side = endpoint.side === 'splice' || endpoint.side === 'patch' ? `:${endpoint.side}` : '';
      return `${CONN_PREFIX}${endpoint.cassetteId}:conn:${endpoint.slotInCassette}${side}`;
    }
    case 'splitter-in':
      return `${CONN_PREFIX}${endpoint.cassetteId}:spl:${endpoint.splitterIndex}:in`;
    case 'splitter-out':
      return `${CONN_PREFIX}${endpoint.cassetteId}:spl:${endpoint.splitterIndex}:out:${endpoint.output}`;
  }
}

export function parseOccupancyEndpointId(id: string | null | undefined): OccupancyEndpoint | null {
  const raw = (id ?? '').trim();
  if (!raw) return null;
  if (raw.startsWith(FIBER_PREFIX)) {
    const rest = raw.slice(FIBER_PREFIX.length);
    const parts = rest.split(':');
    if (parts.length < 3) return null;
    const roleRaw = parts[0];
    const role: OccupancyCableRole | null =
      roleRaw === 'incoming' || roleRaw === 'outgoing' ? roleRaw : null;
    const physicalFiber = Number.parseInt(parts[parts.length - 1] ?? '', 10);
    const cableId = parts.slice(1, -1).join(':');
    if (!cableId || !Number.isInteger(physicalFiber) || physicalFiber < 1) return null;
    return { kind: 'cable-fiber', cableId, physicalFiber, role };
  }
  if (!raw.startsWith(CONN_PREFIX)) return null;
  const body = raw.slice(CONN_PREFIX.length);
  const conn = /^(.+):conn:(\d+)(?::(splice|patch))?$/.exec(body);
  if (conn) {
    const slot = Number.parseInt(conn[2] ?? '', 10);
    if (!conn[1] || !Number.isInteger(slot) || slot < 1) return null;
    const side = conn[3] === 'splice' || conn[3] === 'patch' ? conn[3] : null;
    return {
      kind: 'cassette-connector',
      cassetteId: conn[1],
      slotInCassette: slot,
      ...(side ? { side } : {}),
    };
  }
  const splIn = /^(.+):spl:(\d+):in$/.exec(body);
  if (splIn) {
    const splitterIndex = Number.parseInt(splIn[2] ?? '', 10);
    if (!splIn[1] || !Number.isInteger(splitterIndex) || splitterIndex < 1) return null;
    return { kind: 'splitter-in', cassetteId: splIn[1], splitterIndex };
  }
  const splOut = /^(.+):spl:(\d+):out:(\d+)$/.exec(body);
  if (splOut) {
    const splitterIndex = Number.parseInt(splOut[2] ?? '', 10);
    const output = Number.parseInt(splOut[3] ?? '', 10);
    if (!splOut[1] || !Number.isInteger(splitterIndex) || splitterIndex < 1) return null;
    if (!Number.isInteger(output) || output < 1) return null;
    return { kind: 'splitter-out', cassetteId: splOut[1], splitterIndex, output };
  }
  return null;
}

export function cassetteIdsForEndpoint(endpoint: OccupancyEndpoint | null): string[] {
  if (!endpoint) return [];
  if (endpoint.kind === 'cable-fiber') return [];
  return [endpoint.cassetteId];
}

export function layoutWeldConnections(layout: Pick<RozvadecWeldLayout, 'weldConnections'>): OccupancyWeldConnection[] {
  return [...(layout.weldConnections ?? [])];
}

export function layoutPatchConnections(layout: Pick<RozvadecWeldLayout, 'patchConnections'>): OccupancyPatchConnection[] {
  return [...(layout.patchConnections ?? [])];
}

export function layoutSplitterPaths(layout: Pick<RozvadecWeldLayout, 'splitterPaths'>): OccupancySplitterPath[] {
  return [...(layout.splitterPaths ?? [])];
}

export function emptyCassetteOccupancyFields(): Pick<
  RozvadecWeldLayout,
  'weldConnections' | 'patchConnections' | 'splitterPaths'
> {
  return { weldConnections: [], patchConnections: [], splitterPaths: [] };
}

function newId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function issue(
  code: OccupancyConnectionIssueCode,
  path: string,
  message: string,
  endpointId?: string,
): OccupancyConnectionIssue {
  return { code, path, message, endpointId };
}

function isBuildingCableId(cableId: string): boolean {
  return cableId.startsWith('budova:');
}

export function occupancyFiberIsOutgoing(endpoint: OccupancyEndpoint | null | undefined): boolean {
  if (endpoint?.kind !== 'cable-fiber') return false;
  return endpoint.role === 'outgoing' || isBuildingCableId(endpoint.cableId);
}

function occupancyFiberKey(endpoint: OccupancyEndpoint): string | null {
  if (endpoint.kind !== 'cable-fiber') return null;
  return `${endpoint.cableId}:${endpoint.physicalFiber}`;
}

export function resolveConnectorSide(
  source: OccupancyEndpoint,
  target: OccupancyEndpoint,
): OccupancyConnectorSideName {
  if (source.kind === 'cable-fiber' || target.kind === 'cable-fiber') return 'splice-side';
  return 'patch-side';
}

export function occupancySameConnectorChannel(
  left: OccupancyEndpoint | null | undefined,
  right: OccupancyEndpoint | null | undefined,
): boolean {
  return (
    left?.kind === 'cassette-connector'
    && right?.kind === 'cassette-connector'
    && left.cassetteId === right.cassetteId
    && left.slotInCassette === right.slotInCassette
  );
}

export function adapterThroughPeerId(endpointId: string): string | null {
  const parsed = parseOccupancyEndpointId(endpointId);
  if (parsed?.kind !== 'cassette-connector') return null;
  const side: OccupancyConnectorSide = parsed.side === 'patch' ? 'splice' : 'patch';
  return occupancyEndpointId({ ...parsed, side });
}

export function connectorChannelsForCassette(cassette: RozvadecWeldCassette): OccupancyConnectorChannel[] {
  const def = getRozvadecCassetteType(cassette.cassetteType);
  if (!def?.adapterFamily || def.connectorPortCount == null) return [];
  const family = def.adapterFamily;
  const connectorType = family === 'LC' ? 'LC/APC' : 'SC/APC';
  const channels: OccupancyConnectorChannel[] = [];
  for (let port = 1; port <= def.connectorPortCount; port += 1) {
    const adapterPosition = rozvadecKm5AdapterPosition(family, port) ?? port;
    channels.push({
      id: `${CONN_PREFIX}${cassette.id}:ch:${port}`,
      cassetteId: cassette.id,
      adapterPosition,
      channel: rozvadecKm5ConnectorPolarity(family, port),
      connectorType,
      spliceSideEndpointId: occupancyEndpointId({
        kind: 'cassette-connector',
        cassetteId: cassette.id,
        slotInCassette: port,
        side: 'splice',
      }),
      patchSideEndpointId: occupancyEndpointId({
        kind: 'cassette-connector',
        cassetteId: cassette.id,
        slotInCassette: port,
        side: 'patch',
      }),
    });
  }
  return channels;
}

function connectorChannelKey(endpoint: OccupancyEndpoint): string | null {
  if (endpoint.kind !== 'cassette-connector') return null;
  return `${endpoint.cassetteId}#${endpoint.slotInCassette}`;
}

function withConnectorSide(endpoint: OccupancyEndpoint, side: OccupancyConnectorSide): OccupancyEndpoint {
  if (endpoint.kind !== 'cassette-connector') return endpoint;
  return { ...endpoint, side };
}

function writeEndpointId(endpoint: OccupancyEndpoint, side: OccupancyConnectorSideName): string {
  if (endpoint.kind !== 'cassette-connector') return occupancyEndpointId(endpoint);
  return occupancyEndpointId(withConnectorSide(endpoint, side === 'splice-side' ? 'splice' : 'patch'));
}

function connectionTouchesChannel(
  fromEndpointId: string,
  toEndpointId: string,
  query: OccupancyEndpoint,
): boolean {
  return [fromEndpointId, toEndpointId].some((id) => occupancySameConnectorChannel(parseOccupancyEndpointId(id), query));
}

export function occupancyEndpointHasWeld(
  layout: Pick<RozvadecWeldLayout, 'weldConnections'>,
  endpointId: string,
): boolean {
  const query = parseOccupancyEndpointId(endpointId);
  if (!query) return false;
  return layoutWeldConnections(layout).some((row) => {
    if (query.kind === 'cassette-connector') {
      return connectionTouchesChannel(row.fromEndpointId, row.toEndpointId, query);
    }
    return row.fromEndpointId === endpointId || row.toEndpointId === endpointId
      || occupancyFiberKey(query) != null
        && [parseOccupancyEndpointId(row.fromEndpointId), parseOccupancyEndpointId(row.toEndpointId)]
          .some((end) => end != null && occupancyFiberKey(end) === occupancyFiberKey(query));
  });
}

export function occupancyEndpointHasPatch(
  layout: Pick<RozvadecWeldLayout, 'patchConnections'>,
  endpointId: string,
): boolean {
  const query = parseOccupancyEndpointId(endpointId);
  if (!query) return false;
  return layoutPatchConnections(layout).some((row) => {
    if (query.kind === 'cassette-connector') {
      return connectionTouchesChannel(row.fromEndpointId, row.toEndpointId, query);
    }
    return row.fromEndpointId === endpointId || row.toEndpointId === endpointId;
  });
}

export type OccupancyStoredConnectionKind = 'weld' | 'patch';

function occupancyRowTouchesQuery(
  fromEndpointId: string,
  toEndpointId: string,
  query: OccupancyEndpoint,
  endpointId: string,
): boolean {
  if (fromEndpointId === endpointId || toEndpointId === endpointId) return true;
  if (query.kind === 'cassette-connector') {
    return connectionTouchesChannel(fromEndpointId, toEndpointId, query);
  }
  if (query.kind === 'cable-fiber') {
    const key = occupancyFiberKey(query);
    return [parseOccupancyEndpointId(fromEndpointId), parseOccupancyEndpointId(toEndpointId)].some(
      (end) => end != null && occupancyFiberKey(end) === key,
    );
  }
  return false;
}

function occupancyKindForEndpoint(
  query: OccupancyEndpoint,
  kind?: OccupancyStoredConnectionKind,
): OccupancyStoredConnectionKind | null {
  if (kind) return kind;
  if (query.kind === 'cable-fiber') return 'weld';
  // PON32 / KM6: fiber ↔ splitter IN is a weld. KM5: SC ↔ IN is a patch.
  // Unspecified kind searches both stored rows (same as mobile OccupancyConnection).
  if (query.kind === 'splitter-in' || query.kind === 'splitter-out') return null;
  if (query.kind === 'cassette-connector') {
    if (query.side === 'splice') return 'weld';
    if (query.side === 'patch') return 'patch';
    return null;
  }
  return null;
}

/**
 * Stored weld / patch row for an endpoint or connection id.
 * Unsided connector ids and implicit adapter-through never resolve — they are not stored rows.
 */
export function occupancyConnectionIdForEndpoint(
  layout: Pick<RozvadecWeldLayout, 'weldConnections' | 'patchConnections'>,
  endpointOrConnectionId: string,
  kind?: OccupancyStoredConnectionKind,
): string | null {
  const raw = endpointOrConnectionId.trim();
  if (!raw) return null;
  const welds = layoutWeldConnections(layout);
  const patches = layoutPatchConnections(layout);
  const byId = (kind === 'patch' ? patches : kind === 'weld' ? welds : [...welds, ...patches])
    .find((row) => row.id === raw);
  if (byId) return byId.id;
  const query = parseOccupancyEndpointId(raw);
  if (!query) return null;
  const resolvedKind = occupancyKindForEndpoint(query, kind);
  if (
    !resolvedKind
    && query.kind !== 'splitter-in'
    && query.kind !== 'splitter-out'
  ) {
    return null;
  }
  const rows = resolvedKind === 'weld'
    ? welds
    : resolvedKind === 'patch'
      ? patches
      : [...welds, ...patches];
  return rows.find((row) => occupancyRowTouchesQuery(row.fromEndpointId, row.toEndpointId, query, raw))?.id ?? null;
}

/** Fiber welded onto an unterminated splitter IN/OUT (PON32 KM6 provaření). */
export function isOccupancyFiberSplitterPortPair(
  left: OccupancyEndpoint | null | undefined,
  right: OccupancyEndpoint | null | undefined,
): boolean {
  if (!left || !right) return false;
  const kinds = new Set([left.kind, right.kind]);
  return kinds.has('cable-fiber') && (kinds.has('splitter-in') || kinds.has('splitter-out'));
}

export function occupancyWeldFiberEnd(
  left: OccupancyEndpoint | null | undefined,
  right: OccupancyEndpoint | null | undefined,
): Extract<OccupancyEndpoint, { kind: 'cable-fiber' }> | null {
  if (left?.kind === 'cable-fiber') return left;
  if (right?.kind === 'cable-fiber') return right;
  return null;
}

function occupancyFiberAlreadyWelded(
  layout: Pick<RozvadecWeldLayout, 'weldConnections'>,
  fiber: OccupancyEndpoint,
): boolean {
  const key = occupancyFiberKey(fiber);
  if (!key) return false;
  return layoutWeldConnections(layout).some((row) => {
    const ends = [parseOccupancyEndpointId(row.fromEndpointId), parseOccupancyEndpointId(row.toEndpointId)];
    return ends.some((end) => end != null && occupancyFiberKey(end) === key);
  });
}

function withInferredFiberRole(endpoint: OccupancyEndpoint): OccupancyEndpoint {
  if (endpoint.kind !== 'cable-fiber' || endpoint.role) return endpoint;
  if (isBuildingCableId(endpoint.cableId)) return { ...endpoint, role: 'outgoing' };
  return endpoint;
}

function fiberRole(endpoint: OccupancyEndpoint | null): OccupancyCableRole | null {
  return endpoint?.kind === 'cable-fiber' ? endpoint.role : null;
}

function connectorHasIncomingFeed(
  layout: RozvadecWeldLayout | null | undefined,
  endpoint: OccupancyEndpoint,
): boolean {
  if (!layout || endpoint.kind !== 'cassette-connector') return false;
  const term = layout.terminations.find(
    (item) =>
      item.cassetteId === endpoint.cassetteId
      && item.slotInCassette === endpoint.slotInCassette
      && item.incoming,
  );
  if (term?.incoming) return true;
  for (const weld of layoutWeldConnections(layout)) {
    const ends = [parseOccupancyEndpointId(weld.fromEndpointId), parseOccupancyEndpointId(weld.toEndpointId)];
    const fiber = ends.find((item) => item?.kind === 'cable-fiber');
    const conn = ends.find((item) => occupancySameConnectorChannel(item, endpoint));
    if (!fiber || !conn || fiber.kind !== 'cable-fiber') continue;
    if (fiber.role === 'outgoing' || isBuildingCableId(fiber.cableId)) continue;
    return true;
  }
  return false;
}

function connectorHasOutgoingFeed(
  layout: RozvadecWeldLayout | null | undefined,
  endpoint: OccupancyEndpoint,
): boolean {
  if (!layout || endpoint.kind !== 'cassette-connector') return false;
  for (const weld of layoutWeldConnections(layout)) {
    const ends = [parseOccupancyEndpointId(weld.fromEndpointId), parseOccupancyEndpointId(weld.toEndpointId)];
    const fiber = ends.find((item) => item?.kind === 'cable-fiber');
    const conn = ends.find((item) => occupancySameConnectorChannel(item, endpoint));
    if (!fiber || !conn || fiber.kind !== 'cable-fiber') continue;
    if (fiber.role === 'outgoing' || isBuildingCableId(fiber.cableId)) return true;
  }
  return false;
}

function isOccupiedAsPathTo(
  layout: RozvadecWeldLayout | null | undefined,
  endpoint: OccupancyEndpoint,
): boolean {
  if (!layout) return false;
  const id = occupancyEndpointId(endpoint);
  const touchesTo = (row: { toEndpointId: string }): boolean => {
    if (row.toEndpointId === id) return true;
    return occupancySameConnectorChannel(parseOccupancyEndpointId(row.toEndpointId), endpoint);
  };
  return layoutWeldConnections(layout).some(touchesTo) || layoutPatchConnections(layout).some(touchesTo);
}

export type OccupancyOrientResult =
  | { kind: 'ok'; from: OccupancyEndpoint; to: OccupancyEndpoint }
  | { kind: 'needs-role'; a: OccupancyEndpoint; b: OccupancyEndpoint }
  | { kind: 'reject'; issue: OccupancyConnectionIssue };

/**
 * Direction from CableRoleAtCabinet (incoming / outgoing), then walk existing
 * welds, adapter-through, patchcords, and splitter IN→OUT. Click order, SC vs LC,
 * and connector side never decide. Unknown fiber role may prompt; two connectors
 * with no incoming fiber are CONNECTION_DIRECTION_UNKNOWN.
 */
export function orientOccupancyEndpoints(
  a: OccupancyEndpoint,
  b: OccupancyEndpoint,
  layout?: RozvadecWeldLayout | null,
): OccupancyOrientResult {
  const left = withInferredFiberRole(a);
  const right = withInferredFiberRole(b);
  if (left.kind === 'splitter-out' && right.kind === 'splitter-in') {
    return {
      kind: 'reject',
      issue: issue('SPLITTER_OUT_USED_AS_IN', 'connection', 'Splitter výstup nelze použít jako vstup.', occupancyEndpointId(left)),
    };
  }
  if (right.kind === 'splitter-out' && left.kind === 'splitter-in') {
    return {
      kind: 'reject',
      issue: issue('SPLITTER_OUT_USED_AS_IN', 'connection', 'Splitter výstup nelze použít jako vstup.', occupancyEndpointId(right)),
    };
  }
  const roleA = fiberRole(left);
  const roleB = fiberRole(right);
  if (roleA === 'incoming' && roleB === 'outgoing') return { kind: 'ok', from: left, to: right };
  if (roleB === 'incoming' && roleA === 'outgoing') return { kind: 'ok', from: right, to: left };
  if (roleA === 'incoming') return { kind: 'ok', from: left, to: right };
  if (roleB === 'incoming') return { kind: 'ok', from: right, to: left };
  if (roleA === 'outgoing') {
    if (right.kind === 'splitter-in') {
      return {
        kind: 'reject',
        issue: issue('OUTGOING_AS_PATH_START', occupancyEndpointId(left), 'Výstupní vlákno nemůže začínat cestu.'),
      };
    }
    return { kind: 'ok', from: right, to: left };
  }
  if (roleB === 'outgoing') {
    if (left.kind === 'splitter-in') {
      return {
        kind: 'reject',
        issue: issue('OUTGOING_AS_PATH_START', occupancyEndpointId(right), 'Výstupní vlákno nemůže začínat cestu.'),
      };
    }
    return { kind: 'ok', from: left, to: right };
  }
  if (left.kind === 'splitter-in') return { kind: 'ok', from: right, to: left };
  if (right.kind === 'splitter-in') return { kind: 'ok', from: left, to: right };
  if (left.kind === 'splitter-out') return { kind: 'ok', from: left, to: right };
  if (right.kind === 'splitter-out') return { kind: 'ok', from: right, to: left };

  const startA = connectorHasIncomingFeed(layout, left) || isOccupiedAsPathTo(layout, left);
  const startB = connectorHasIncomingFeed(layout, right) || isOccupiedAsPathTo(layout, right);
  if (startA && !startB) return { kind: 'ok', from: left, to: right };
  if (startB && !startA) return { kind: 'ok', from: right, to: left };
  const endA = connectorHasOutgoingFeed(layout, left);
  const endB = connectorHasOutgoingFeed(layout, right);
  if (endA && !endB) return { kind: 'ok', from: right, to: left };
  if (endB && !endA) return { kind: 'ok', from: left, to: right };

  if (left.kind === 'cable-fiber' || right.kind === 'cable-fiber') {
    return { kind: 'needs-role', a: left, b: right };
  }
  return {
    kind: 'reject',
    issue: issue(
      'CONNECTION_DIRECTION_UNKNOWN',
      'connection',
      'Nelze určit směr cesty — chybí vstupní vlákno.',
    ),
  };
}

export function remapOccupancyEndpointId(
  endpointId: string,
  cassetteIdMap: ReadonlyMap<string, string>,
): string {
  const parsed = parseOccupancyEndpointId(endpointId);
  if (!parsed || parsed.kind === 'cable-fiber') return endpointId;
  const cassetteId = cassetteIdMap.get(parsed.cassetteId) ?? parsed.cassetteId;
  if (cassetteId === parsed.cassetteId) return endpointId;
  return occupancyEndpointId({ ...parsed, cassetteId });
}

export function remapLayoutOccupancyIds(
  layout: Pick<RozvadecWeldLayout, 'weldConnections' | 'patchConnections' | 'splitterPaths'>,
  cassetteIdMap: ReadonlyMap<string, string>,
  connectionIdMap: ReadonlyMap<string, string> = new Map(),
): Pick<RozvadecWeldLayout, 'weldConnections' | 'patchConnections' | 'splitterPaths'> {
  const remapConn = (id: string): string => connectionIdMap.get(id) ?? id;
  const remapEnd = (id: string): string => remapOccupancyEndpointId(id, cassetteIdMap);
  return {
    weldConnections: layoutWeldConnections(layout).map((row) => ({
      ...row,
      id: remapConn(row.id),
      fromEndpointId: remapEnd(row.fromEndpointId),
      toEndpointId: remapEnd(row.toEndpointId),
    })),
    patchConnections: layoutPatchConnections(layout).map((row) => ({
      id: remapConn(row.id),
      fromEndpointId: remapEnd(row.fromEndpointId),
      toEndpointId: remapEnd(row.toEndpointId),
    })),
    splitterPaths: layoutSplitterPaths(layout).map((row) => ({
      ...row,
      id: remapConn(row.id),
      cassetteId: cassetteIdMap.get(row.cassetteId) ?? row.cassetteId,
      inputPatchId: row.inputPatchId ? remapConn(row.inputPatchId) : null,
      outputPatches: (row.outputPatches ?? []).map((item) => ({
        ...item,
        patchId: item.patchId ? remapConn(item.patchId) : null,
      })),
    })),
  };
}

function connectorFamily(layout: RozvadecWeldLayout, cassetteId: string): string | null {
  const cassette = layout.cassettes.find((item) => item.id === cassetteId);
  return cassette ? getRozvadecCassetteType(cassette.cassetteType)?.adapterFamily ?? null : null;
}

function endpointsCompatible(layout: RozvadecWeldLayout, from: OccupancyEndpoint, to: OccupancyEndpoint): boolean {
  if (from.kind === 'cassette-connector' && to.kind === 'cassette-connector') {
    const a = connectorFamily(layout, from.cassetteId);
    const b = connectorFamily(layout, to.cassetteId);
    if (a && b && a !== b) return false;
  }
  return true;
}

function occupiedPortIds(layout: RozvadecWeldLayout): Map<string, string> {
  const used = new Map<string, string>();
  const mark = (id: string, connectionId: string): void => {
    used.set(id, connectionId);
  };
  for (const weld of layoutWeldConnections(layout)) {
    mark(weld.fromEndpointId, weld.id);
    mark(weld.toEndpointId, weld.id);
  }
  for (const patch of layoutPatchConnections(layout)) {
    mark(patch.fromEndpointId, patch.id);
    mark(patch.toEndpointId, patch.id);
  }
  return used;
}

export function validateCassetteOccupancyConnections(
  layout: RozvadecWeldLayout,
  options?: { draft?: boolean; allowDirectFiberFiber?: boolean },
): OccupancyConnectionIssue[] {
  const issues: OccupancyConnectionIssue[] = [];
  const welds = layoutWeldConnections(layout);
  const patches = layoutPatchConnections(layout);
  const paths = layoutSplitterPaths(layout);
  const weldPortSeen = new Map<string, string>();
  const weldFiberSeen = new Map<string, string>();
  const touchWeld = (endpointId: string, connectionId: string): void => {
    const parsed = parseOccupancyEndpointId(endpointId);
    if (!parsed) {
      issues.push(issue('ENDPOINT_UNKNOWN', connectionId, 'Neznámý koncový bod.', endpointId));
      return;
    }
    if (parsed.kind === 'cable-fiber') {
      const key = occupancyFiberKey(parsed);
      if (!key) return;
      const prev = weldFiberSeen.get(key);
      if (prev && prev !== connectionId) {
        issues.push(issue('WELD_FIBER_OCCUPIED', connectionId, 'Toto vlákno už je svařené.', endpointId));
      }
      weldFiberSeen.set(key, connectionId);
      return;
    }
    if (parsed.kind !== 'cassette-connector') return;
    const key = connectorChannelKey(parsed) ?? endpointId;
    const prev = weldPortSeen.get(key);
    if (prev && prev !== connectionId) {
      issues.push(
        issue('WELD_PORT_OCCUPIED', connectionId, 'Tento kanál už má svar (splice-side).', endpointId),
      );
    }
    weldPortSeen.set(key, connectionId);
  };

  for (const weld of welds) {
    const from = parseOccupancyEndpointId(weld.fromEndpointId);
    const to = parseOccupancyEndpointId(weld.toEndpointId);
    if (from && to && isOccupancyFiberFiberPair(from, to) && !options?.allowDirectFiberFiber) {
      issues.push(
        issue(
          'DIRECT_CABLE_CABLE_FORBIDDEN',
          weld.id,
          'Přímo kabel na kabel lze svařit jen v kazetě nebo na ploše optické spojky.',
        ),
      );
    }
    if (from?.kind === 'cable-fiber' && from.role === 'outgoing' && to?.kind === 'splitter-in') {
      issues.push(issue('OUTGOING_AS_PATH_START', weld.id, 'Výstupní vlákno nemůže začínat cestu.', weld.fromEndpointId));
    }
    touchWeld(weld.fromEndpointId, weld.id);
    touchWeld(weld.toEndpointId, weld.id);
  }

  const patchPortSeen = new Map<string, string>();
  for (const patch of patches) {
    const from = parseOccupancyEndpointId(patch.fromEndpointId);
    const to = parseOccupancyEndpointId(patch.toEndpointId);
    if (!from || !to) {
      issues.push(issue('ENDPOINT_UNKNOWN', patch.id, 'Neznámý koncový bod.'));
      continue;
    }
    if (from.kind === 'splitter-out' && to.kind === 'splitter-in') {
      issues.push(issue('SPLITTER_OUT_USED_AS_IN', patch.id, 'Splitter výstup nelze použít jako vstup.', patch.fromEndpointId));
    }
    if (!endpointsCompatible(layout, from, to)) {
      issues.push(issue('CONNECTOR_TYPE_INCOMPATIBLE', patch.id, 'Nekompatibilní typy konektorů (SC / LC).'));
    }
    for (const end of [patch.fromEndpointId, patch.toEndpointId]) {
      const parsed = parseOccupancyEndpointId(end);
      if (!parsed || parsed.kind === 'cable-fiber') continue;
      const key = connectorChannelKey(parsed) ?? end;
      const prev = patchPortSeen.get(key);
      if (prev && prev !== patch.id) {
        issues.push(
          issue(
            parsed.kind === 'splitter-out' ? 'SPLITTER_OUT_OCCUPIED' : 'PATCH_PORT_OCCUPIED',
            patch.id,
            parsed.kind === 'splitter-out'
              ? 'Tento výstup splitteru už je napojený.'
              : 'Tento kanál už má patchcord (patch-side).',
            end,
          ),
        );
      }
      patchPortSeen.set(key, patch.id);
    }
  }

  if (!options?.draft) {
    for (const path of paths) {
      const hasIn = !!path.inputPatchId;
      const outs = (path.outputPatches ?? []).filter((item) => item.patchId);
      if (hasIn !== outs.length > 0 && (hasIn || outs.length > 0)) {
        issues.push(
          issue(
            'SPLITTER_PATH_INCOMPLETE',
            path.id,
            'Cesta přes splitter je neúplná — chybí vstup nebo výstup.',
          ),
        );
      }
    }
    issues.push(...detectCyclicSplitterPaths(patches));
  }
  return issues;
}

function detectCyclicSplitterPaths(patches: readonly OccupancyPatchConnection[]): OccupancyConnectionIssue[] {
  const graph = new Map<string, string[]>();
  for (const patch of patches) {
    const list = graph.get(patch.fromEndpointId) ?? [];
    list.push(patch.toEndpointId);
    graph.set(patch.fromEndpointId, list);
  }
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const issues: OccupancyConnectionIssue[] = [];
  const walk = (node: string): boolean => {
    if (visiting.has(node)) return true;
    if (visited.has(node)) return false;
    visiting.add(node);
    for (const next of graph.get(node) ?? []) {
      if (walk(next)) return true;
    }
    visiting.delete(node);
    visited.add(node);
    return false;
  };
  for (const node of graph.keys()) {
    if (walk(node)) {
      issues.push(issue('SPLITTER_PATH_CYCLIC', node, 'Cesta přes splitter je cyklická.', node));
      break;
    }
  }
  return issues;
}

export type OccupancyWriteResult =
  | { ok: true; layout: RozvadecWeldLayout }
  | { ok: false; issues: OccupancyConnectionIssue[] };

function withOccupancy(
  layout: RozvadecWeldLayout,
  patch: Partial<Pick<RozvadecWeldLayout, 'weldConnections' | 'patchConnections' | 'splitterPaths'>>,
): RozvadecWeldLayout {
  return {
    ...layout,
    weldConnections: patch.weldConnections ?? layoutWeldConnections(layout),
    patchConnections: patch.patchConnections ?? layoutPatchConnections(layout),
    splitterPaths: patch.splitterPaths ?? layoutSplitterPaths(layout),
  };
}

function upsertSplitterPath(
  paths: OccupancySplitterPath[],
  cassetteId: string,
  splitterIndex: number,
  mutate: (path: OccupancySplitterPath) => OccupancySplitterPath,
): OccupancySplitterPath[] {
  const index = paths.findIndex(
    (item) => item.cassetteId === cassetteId && item.splitterIndex === splitterIndex,
  );
  const current: OccupancySplitterPath =
    index >= 0
      ? paths[index]
      : { id: newId('spath'), cassetteId, splitterIndex, inputPatchId: null, outputPatches: [] };
  const next = mutate(current);
  if (index >= 0) {
    const copy = [...paths];
    copy[index] = next;
    return copy;
  }
  return [...paths, next];
}

export function isOccupancyFiberFiberPair(
  left: OccupancyEndpoint,
  right: OccupancyEndpoint,
): boolean {
  return left.kind === 'cable-fiber' && right.kind === 'cable-fiber';
}

export function addOccupancyWeldConnection(
  layout: RozvadecWeldLayout,
  left: OccupancyEndpoint,
  right: OccupancyEndpoint,
  assignedRole?: OccupancyCableRole | null,
  options?: { allowDirectFiberFiber?: boolean },
): OccupancyWriteResult {
  const a = applyRole(left, assignedRole);
  const b = right;
  if (isOccupancyFiberFiberPair(a, b) && !options?.allowDirectFiberFiber) {
    return {
      ok: false,
      issues: [
        issue(
          'DIRECT_CABLE_CABLE_FORBIDDEN',
          'connection',
          'Přímo kabel na kabel lze svařit jen v kazetě nebo na ploše optické spojky.',
        ),
      ],
    };
  }
  const oriented = { from: a, to: b };
  const side = resolveConnectorSide(oriented.from, oriented.to);
  const endpointIds = [
    writeEndpointId(oriented.from, side),
    writeEndpointId(oriented.to, side),
  ].sort((left, right) => left.localeCompare(right));
  const [fromId, toId] = endpointIds;
  if (fromId === toId || occupancyFiberKey(oriented.from) != null
    && occupancyFiberKey(oriented.from) === occupancyFiberKey(oriented.to)) {
    return {
      ok: false,
      issues: [issue('WELD_FIBER_OCCUPIED', 'connection', 'Vlákno nelze svařit samo se sebou.', fromId)],
    };
  }
  for (const end of [oriented.from, oriented.to]) {
    const id = occupancyEndpointId(end);
    if (end.kind === 'cassette-connector' && occupancyEndpointHasWeld(layout, id)) {
      return {
        ok: false,
        issues: [issue('WELD_PORT_OCCUPIED', id, 'Tento kanál už má svar (splice-side).', id)],
      };
    }
    if (end.kind === 'cable-fiber' && occupancyFiberAlreadyWelded(layout, end)) {
      return {
        ok: false,
        issues: [issue('WELD_FIBER_OCCUPIED', id, 'Toto vlákno už je svařené.', id)],
      };
    }
  }
  const row: OccupancyWeldConnection = {
    id: newId('weld'),
    fromEndpointId: fromId as string,
    toEndpointId: toId as string,
  };
  return { ok: true, layout: withOccupancy(layout, { weldConnections: [...layoutWeldConnections(layout), row] }) };
}

function applyRole(endpoint: OccupancyEndpoint, role: OccupancyCableRole | null | undefined): OccupancyEndpoint {
  if (endpoint.kind !== 'cable-fiber' || !role) return endpoint;
  return { ...endpoint, role };
}

export function addOccupancyPatchConnection(
  layout: RozvadecWeldLayout,
  left: OccupancyEndpoint,
  right: OccupancyEndpoint,
): OccupancyWriteResult {
  if (!endpointsCompatible(layout, left, right)) {
    return {
      ok: false,
      issues: [issue('CONNECTOR_TYPE_INCOMPATIBLE', 'connection', 'Nekompatibilní typy konektorů (SC / LC).')],
    };
  }
  const oriented = orientOccupancyEndpoints(left, right, layout);
  if (oriented.kind === 'needs-role') {
    return {
      ok: false,
      issues: [
        issue('ORIENTATION_NEEDS_CABLE_ROLE', 'connection', 'Určete roli kabelu (vstupní / výstupní).'),
      ],
    };
  }
  if (oriented.kind === 'reject') return { ok: false, issues: [oriented.issue] };
  if (!endpointsCompatible(layout, oriented.from, oriented.to)) {
    return {
      ok: false,
      issues: [issue('CONNECTOR_TYPE_INCOMPATIBLE', 'connection', 'Nekompatibilní typy konektorů (SC / LC).')],
    };
  }
  const side = resolveConnectorSide(oriented.from, oriented.to);
  const fromId = writeEndpointId(oriented.from, side);
  const toId = writeEndpointId(oriented.to, side);
  const occupied = occupiedPortIds(layout);
  for (const end of [fromId, toId]) {
    const parsed = parseOccupancyEndpointId(end);
    if (!parsed || parsed.kind === 'cable-fiber') continue;
    if (parsed.kind === 'splitter-out' && occupied.has(end)) {
      return {
        ok: false,
        issues: [issue('SPLITTER_OUT_OCCUPIED', end, 'Tento výstup splitteru už je napojený.', end)],
      };
    }
    if (parsed.kind === 'splitter-in' && occupied.has(end)) {
      return {
        ok: false,
        issues: [issue('PATCH_PORT_OCCUPIED', end, 'Tento kanál už má patchcord (patch-side).', end)],
      };
    }
    if (parsed.kind === 'cassette-connector' && occupancyEndpointHasPatch(layout, end)) {
      return {
        ok: false,
        issues: [issue('PATCH_PORT_OCCUPIED', end, 'Tento kanál už má patchcord (patch-side).', end)],
      };
    }
  }
  const row: OccupancyPatchConnection = { id: newId('patch'), fromEndpointId: fromId, toEndpointId: toId };
  let paths = layoutSplitterPaths(layout);
  const splitterEnd =
    oriented.from.kind === 'splitter-in' || oriented.from.kind === 'splitter-out'
      ? oriented.from
      : oriented.to.kind === 'splitter-in' || oriented.to.kind === 'splitter-out'
        ? oriented.to
        : null;
  if (splitterEnd?.kind === 'splitter-in') {
    paths = upsertSplitterPath(paths, splitterEnd.cassetteId, splitterEnd.splitterIndex, (path) => ({
      ...path,
      inputPatchId: row.id,
    }));
  }
  if (splitterEnd?.kind === 'splitter-out') {
    paths = upsertSplitterPath(paths, splitterEnd.cassetteId, splitterEnd.splitterIndex, (path) => {
      const rest = path.outputPatches.filter((item) => item.output !== splitterEnd.output);
      return { ...path, outputPatches: [...rest, { output: splitterEnd.output, patchId: row.id }] };
    });
  }
  return {
    ok: true,
    layout: withOccupancy(layout, {
      patchConnections: [...layoutPatchConnections(layout), row],
      splitterPaths: paths,
    }),
  };
}

export function removeOccupancyConnection(layout: RozvadecWeldLayout, connectionId: string): RozvadecWeldLayout {
  const welds = layoutWeldConnections(layout).filter((item) => item.id !== connectionId);
  const patches = layoutPatchConnections(layout).filter((item) => item.id !== connectionId);
  const paths = layoutSplitterPaths(layout).map((path) => ({
    ...path,
    inputPatchId: path.inputPatchId === connectionId ? null : path.inputPatchId,
    outputPatches: path.outputPatches.map((item) =>
      item.patchId === connectionId ? { ...item, patchId: null } : item,
    ),
  }));
  return withOccupancy(layout, { weldConnections: welds, patchConnections: patches, splitterPaths: paths });
}
